import { useEffect, useMemo, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { motion } from "framer-motion";
import { Plus, Trash2, Printer } from "lucide-react";

import { CalculatorLayout } from "@/components/calculator/CalculatorLayout";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useAppStore } from "@/lib/store";
import { toBengaliNumerals } from "@/lib/bdNumerals";
import { formatBDT } from "@/lib/bdCurrency";
import { buildSeo } from "@/lib/seo";
import { cn } from "@/lib/utils";

import {
  rectangleSqft,
  triangleBaseHeightSqft,
  triangleHeronSqft,
  circleSqft,
  totalRoomsSqft,
  computeFlooring,
  computePaint,
  computeCarpet,
  computeLandCost,
  fromSqft,
  type LenUnit,
  type Room,
} from "@/lib/calculators/squareFeet";

type B = { en: string; bn: string };
type Lang = "en" | "bn";
type TabId = "rect" | "tri" | "circle" | "rooms" | "practical";
type SubTab = "flooring" | "paint" | "land" | "carpet";

const T = {
  h1: { en: "Square Feet Calculator", bn: "স্কয়ার ফিট ক্যালকুলেটর" },
  desc: {
    en: "Calculate area in square feet for rectangles, triangles, circles, multi-room floor plans, plus tile, paint and carpet estimators tuned for Bangladesh.",
    bn: "আয়তক্ষেত্র, ত্রিভুজ, বৃত্ত ও মাল্টি-রুম প্ল্যানের আয়তন বর্গফুটে; সঙ্গে টাইলস, রঙ ও কার্পেট হিসাব — বাংলাদেশের জন্য।",
  },
  bnDigits: { en: "Bengali numerals", bn: "বাংলা সংখ্যা" },
  print: { en: "Print", bn: "প্রিন্ট" },
  rect: { en: "Rectangle", bn: "আয়তক্ষেত্র" },
  tri: { en: "Triangle", bn: "ত্রিভুজ" },
  circle: { en: "Circle", bn: "বৃত্ত" },
  rooms: { en: "Multi-room", bn: "মাল্টি-রুম" },
  practical: { en: "Practical", bn: "প্রায়োগিক" },
} as const;

const FAQ: { q: B; a: B }[] = [
  { q: { en: "How do I calculate square feet of a room?", bn: "একটি রুমের স্কয়ার ফিট কীভাবে বের করব?" },
    a: { en: "For a rectangular room, multiply length × width in feet. A 20 ft × 12 ft room = 240 sq ft. If your measurements are in inches, divide by 12 first; if in meters, multiply ft = m × 3.28084.",
         bn: "আয়তাকার রুমের জন্য দৈর্ঘ্য × প্রস্থ (ফুটে) গুণ করুন। ২০ × ১২ ফুট = ২৪০ বর্গফুট। মাপ ইঞ্চিতে থাকলে ১২ দিয়ে ভাগ; মিটার হলে × ৩.২৮০৮৪ করুন।" } },
  { q: { en: "How many tiles do I need for a 1000 sq ft room?", bn: "১০০০ স্কয়ার ফিট রুমের জন্য কত টাইলস?" },
    a: { en: "Depends on tile size. With 12\" × 12\" tiles (1 sq ft each): 1000 ÷ 1 × 1.10 wastage ≈ 1,100 tiles. With 24\" × 24\" tiles (4 sq ft each): 1000 ÷ 4 × 1.10 ≈ 275 tiles. Use the Flooring tab.",
         bn: "টাইলের সাইজের ওপর নির্ভর করে। ১২\"×১২\" (১ বর্গফুট) → ১,১০০ টাইলস (১০% অপচয় ধরে)। ২৪\"×২৪\" (৪ বর্গফুট) → ২৭৫ টাইলস। 'প্রায়োগিক' ট্যাবে হিসাব করুন।" } },
  { q: { en: "How is square feet different from square meters?", bn: "স্কয়ার ফিট ও স্কয়ার মিটারের পার্থক্য?" },
    a: { en: "1 sq m = 10.7639 sq ft. So a 240 sq ft room ≈ 22.30 sq m. Bangladesh real-estate brochures usually quote sq ft; government records may use sq m or Decimal.",
         bn: "১ বর্গমিটার = ১০.৭৬৩৯ বর্গফুট। ২৪০ বর্গফুট ≈ ২২.৩০ বর্গমিটার। বাংলাদেশে রিয়েল এস্টেট সাধারণত বর্গফুটে, সরকারি রেকর্ডে বর্গমিটার বা ডেসিমেলে।" } },
  { q: { en: "What is a cubit (হাত) in feet?", bn: "১ হাত কত ফুট?" },
    a: { en: "1 cubit (hath) ≈ 1.5 ft. So 1 sq cubit = 2.25 sq ft. Older homes in Bangladesh were often measured in hath; this calculator supports cubit input directly.",
         bn: "১ হাত ≈ ১.৫ ফুট। ১ বর্গহাত = ২.২৫ বর্গফুট। বাংলাদেশের পুরোনো বাড়িগুলো প্রায়ই হাতে মাপা হত — এই ক্যালকুলেটর সরাসরি হাত সাপোর্ট করে।" } },
  { q: { en: "How much paint is needed for 1000 sq ft of wall?", bn: "১০০০ বর্গফুট দেয়ালে কত রঙ লাগে?" },
    a: { en: "At typical 350 sq ft/L coverage for emulsion (1 coat), 2 coats need (1000 × 2) ÷ 350 ≈ 5.7 L → 6 × 1L cans. Berger / Asian Paints / Roxy publish exact coverage on the can — use that for accuracy.",
         bn: "ইমালশনের সাধারণ কভারেজ ৩৫০ বর্গফুট/লিটার (১ কোট)। ২ কোটে (১০০০ × ২) ÷ ৩৫০ ≈ ৫.৭ লি → ৬ × ১ লি কান। নির্দিষ্ট কভারেজ Berger / Asian Paints / Roxy কৌটায় থাকে।" } },
  { q: { en: "Why add 10% wastage for tiles?", bn: "টাইলে ১০% অপচয় কেন ধরা হয়?" },
    a: { en: "Cuts at corners, breakage during transport/installation, and matching pattern at edges all consume extra tiles. 10% is the BD industry default; for diagonal layouts use 15%.",
         bn: "কোণে কাটা, পরিবহন/বসানোতে ভাঙা, এবং প্যাটার্ন মেলাতে অতিরিক্ত টাইল লাগে। ১০% বাংলাদেশের সাধারণ ধারা; কোণাকুণি বসালে ১৫% ধরুন।" } },
  { q: { en: "How do I convert sq ft to katha or decimal?", bn: "বর্গফুট থেকে কাঠা বা ডেসিমেল?" },
    a: { en: "Divide by 720 for katha, by 435.6 for decimal. E.g. 1,440 sq ft = 2 katha = 3.306 decimal. The Land Calculator does the full conversion across all BD units.",
         bn: "৭২০ দিয়ে ভাগ করলে কাঠা, ৪৩৫.৬ দিয়ে ভাগ করলে ডেসিমেল। যেমন ১,৪৪০ বর্গফুট = ২ কাঠা = ৩.৩০৬ ডেসিমেল। সব এককে রূপান্তরের জন্য 'জমির হিসাব ক্যালকুলেটর' দেখুন।" } },
  { q: { en: "How is the area of an irregular room calculated?", bn: "অনিয়মিত রুমের আয়তন কীভাবে?" },
    a: { en: "Split it into rectangles and/or triangles, calculate each separately, then sum. Use the Multi-room tab to add each segment as a 'room'.",
         bn: "রুমটিকে ছোট আয়তক্ষেত্র ও ত্রিভুজে ভাগ করুন, প্রতিটির আয়তন বের করে যোগ করুন। 'মাল্টি-রুম' ট্যাবে প্রতিটি অংশ আলাদা রুম হিসেবে যোগ করুন।" } },
];

export const Route = createFileRoute("/square-feet-calculator")({
  head: () => {
    const seo = buildSeo({
      title: "Square Feet Calculator — Area, Flooring, Paint",
      description:
        "Free square feet calculator. Calculate area for rectangle, triangle, circle. Tile, paint, flooring estimator for Bangladesh. স্কয়ার ফিট ক্যালকুলেটর।",
      path: "/square-feet-calculator",
      breadcrumbs: [
        { name: "Home", path: "/" },
        { name: "Square Feet Calculator", path: "/square-feet-calculator" },
      ],
      faq: FAQ.map((f) => ({ q: f.q.en, a: f.a.en })),
    });
    return { meta: seo.meta, links: seo.links, scripts: seo.scripts };
  },
  component: SquareFeetPage,
});

const STORAGE_KEY = "bcalc.sqft.rooms.v1";

const HASH_TO_TAB: Record<string, TabId> = {
  rect: "rect", tri: "tri", circle: "circle", rooms: "rooms", practical: "practical",
};

function SquareFeetPage() {
  const lang = useAppStore((s) => s.lang) as Lang;
  const tr = (k: keyof typeof T) => (T[k] as B)[lang];
  const [bnDigits, setBnDigits] = useState(false);
  const useBn = lang === "bn" || bnDigits;
  const num = (n: number | string) => (useBn ? toBengaliNumerals(n) : String(n));
  const fmt = (n: number, d = 2) => {
    if (!Number.isFinite(n)) return "—";
    const s = (Math.round(n * Math.pow(10, d)) / Math.pow(10, d)).toLocaleString("en-US", { maximumFractionDigits: d });
    return useBn ? toBengaliNumerals(s) : s;
  };

  const [tab, setTab] = useState<TabId>("rect");
  useEffect(() => {
    if (typeof window === "undefined") return;
    const h = window.location.hash.replace("#", "");
    if (HASH_TO_TAB[h]) setTab(HASH_TO_TAB[h]);
  }, []);
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.location.hash.replace("#", "") !== tab) {
      window.history.replaceState(null, "", `#${tab}`);
    }
  }, [tab]);

  // ---- Tab 1: Rectangle ----
  const [rectLen, setRectLen] = useState("20");
  const [rectWid, setRectWid] = useState("12");
  const [rectUnit, setRectUnit] = useState<LenUnit>("ft");
  const rectArea = useMemo(
    () => rectangleSqft(parseFloat(rectLen) || 0, parseFloat(rectWid) || 0, rectUnit),
    [rectLen, rectWid, rectUnit],
  );

  // ---- Tab 2: Triangle ----
  const [triMethod, setTriMethod] = useState<"bh" | "heron">("bh");
  const [triBase, setTriBase] = useState("10");
  const [triHeight, setTriHeight] = useState("8");
  const [triA, setTriA] = useState("5");
  const [triB, setTriB] = useState("12");
  const [triC, setTriC] = useState("13");
  const [triUnit, setTriUnit] = useState<LenUnit>("ft");
  const triArea = useMemo(() => {
    if (triMethod === "bh") return triangleBaseHeightSqft(parseFloat(triBase) || 0, parseFloat(triHeight) || 0, triUnit);
    return triangleHeronSqft(parseFloat(triA) || 0, parseFloat(triB) || 0, parseFloat(triC) || 0, triUnit);
  }, [triMethod, triBase, triHeight, triA, triB, triC, triUnit]);

  // ---- Tab 3: Circle ----
  const [circKind, setCircKind] = useState<"radius" | "diameter">("radius");
  const [circVal, setCircVal] = useState("10");
  const [circUnit, setCircUnit] = useState<LenUnit>("ft");
  const circ = useMemo(
    () => circleSqft(parseFloat(circVal) || 0, circUnit, circKind),
    [circVal, circUnit, circKind],
  );

  // ---- Tab 4: Multi-room ----
  const [rooms, setRooms] = useState<Room[]>([
    { id: crypto.randomUUID(), name: "Living room", length: 18, width: 14, unit: "ft" },
    { id: crypto.randomUUID(), name: "Bedroom",     length: 12, width: 10, unit: "ft" },
  ]);
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed) && parsed.length) setRooms(parsed);
      }
    } catch { /* ignore */ }
  }, []);
  useEffect(() => {
    if (typeof window === "undefined") return;
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(rooms)); } catch { /* ignore */ }
  }, [rooms]);

  const roomsResult = useMemo(() => totalRoomsSqft(rooms), [rooms]);
  const updateRoom = (id: string, patch: Partial<Room>) =>
    setRooms((rs) => rs.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  const addRoom = () =>
    setRooms((rs) => [...rs, { id: crypto.randomUUID(), name: `Room ${rs.length + 1}`, length: 10, width: 10, unit: "ft" }]);
  const removeRoom = (id: string) => setRooms((rs) => rs.filter((r) => r.id !== id));

  // ---- Tab 5: Practical ----
  const [subTab, setSubTab] = useState<SubTab>("flooring");
  // Flooring
  const [flrSqft, setFlrSqft] = useState("240");
  const [tileLen, setTileLen] = useState("12");
  const [tileWid, setTileWid] = useState("12");
  const [tileWastage, setTileWastage] = useState("10");
  const [tilePrice, setTilePrice] = useState("80");
  const flooring = useMemo(
    () => computeFlooring({
      floorSqft: parseFloat(flrSqft) || 0,
      tileLength: parseFloat(tileLen) || 0,
      tileWidth: parseFloat(tileWid) || 0,
      wastagePct: parseFloat(tileWastage) || 0,
      pricePerTile: parseFloat(tilePrice) || 0,
    }),
    [flrSqft, tileLen, tileWid, tileWastage, tilePrice],
  );
  // Paint
  const [wallSqft, setWallSqft] = useState("850");
  const [coats, setCoats] = useState("2");
  const [coverage, setCoverage] = useState("350");
  const [canSize, setCanSize] = useState("1");
  const [paintPrice, setPaintPrice] = useState("450");
  const paint = useMemo(
    () => computePaint({
      wallSqft: parseFloat(wallSqft) || 0,
      coats: parseFloat(coats) || 0,
      coveragePerLiter: parseFloat(coverage) || 0,
      canSize: parseFloat(canSize) || 1,
      pricePerLiter: parseFloat(paintPrice) || 0,
    }),
    [wallSqft, coats, coverage, canSize, paintPrice],
  );
  // Land
  const [landSqft, setLandSqft] = useState("1440");
  const [landRate, setLandRate] = useState("8000");
  const landCost = computeLandCost(parseFloat(landSqft) || 0, parseFloat(landRate) || 0);
  // Carpet
  const [carpetSqft, setCarpetSqft] = useState("180");
  const [carpetWidth, setCarpetWidth] = useState("12");
  const [carpetPrice, setCarpetPrice] = useState("250");
  const carpet = useMemo(
    () => computeCarpet({
      roomSqft: parseFloat(carpetSqft) || 0,
      carpetWidthFt: parseFloat(carpetWidth) || 0,
      pricePerFt: parseFloat(carpetPrice) || 0,
    }),
    [carpetSqft, carpetWidth, carpetPrice],
  );

  const handlePrint = () => { if (typeof window !== "undefined") window.print(); };

  return (
    <CalculatorLayout
      slug="square-feet-calculator"
      title={tr("h1")}
      description={tr("desc")}
      iconName="Square"
      faq={FAQ.map((f) => ({ q: f.q[lang], a: f.a[lang] }))}
    >
      <div className="mb-6 flex flex-wrap items-center justify-end gap-3 print:hidden">
        <button
          type="button"
          onClick={handlePrint}
          className="inline-flex items-center gap-1.5 rounded-md border border-border bg-card px-3 py-1.5 text-xs hover:border-accent"
        >
          <Printer size={12} /> {tr("print")}
        </button>
        <label className={cn("inline-flex items-center gap-2 text-xs text-muted-foreground", lang === "bn" && "font-bn")}>
          <input type="checkbox" checked={bnDigits} onChange={(e) => setBnDigits(e.target.checked)} className="h-3.5 w-3.5 accent-accent" />
          {tr("bnDigits")}
        </label>
      </div>

      <Tabs value={tab} onValueChange={(v) => setTab(v as TabId)}>
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="rect" className={cn(lang === "bn" && "font-bn")}>{tr("rect")}</TabsTrigger>
          <TabsTrigger value="tri" className={cn(lang === "bn" && "font-bn")}>{tr("tri")}</TabsTrigger>
          <TabsTrigger value="circle" className={cn(lang === "bn" && "font-bn")}>{tr("circle")}</TabsTrigger>
          <TabsTrigger value="rooms" className={cn(lang === "bn" && "font-bn")}>{tr("rooms")}</TabsTrigger>
          <TabsTrigger value="practical" className={cn(lang === "bn" && "font-bn")}>{tr("practical")}</TabsTrigger>
        </TabsList>

        {/* ===== Tab 1: Rectangle ===== */}
        <TabsContent value="rect" className="mt-6 space-y-4">
          <div className="grid gap-3 md:grid-cols-3">
            <NumField label={lang === "en" ? "Length" : "দৈর্ঘ্য"} v={rectLen} on={setRectLen} useBn={useBn} />
            <NumField label={lang === "en" ? "Width" : "প্রস্থ"} v={rectWid} on={setRectWid} useBn={useBn} />
            <UnitToggle value={rectUnit} onChange={setRectUnit} lang={lang} />
          </div>
          <AreaResult sqft={rectArea} fmt={fmt} useBn={useBn} lang={lang} />
        </TabsContent>

        {/* ===== Tab 2: Triangle ===== */}
        <TabsContent value="tri" className="mt-6 space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            {(["bh", "heron"] as const).map((m) => (
              <button
                key={m}
                type="button"
                onClick={() => setTriMethod(m)}
                className={cn(
                  "rounded-full border px-3 py-1.5 text-xs font-medium",
                  triMethod === m
                    ? "border-accent bg-accent text-accent-foreground"
                    : "border-border bg-card hover:border-accent/40",
                  lang === "bn" && "font-bn",
                )}
              >
                {m === "bh"
                  ? (lang === "en" ? "Base + Height" : "ভূমি + উচ্চতা")
                  : (lang === "en" ? "3 sides (Heron)" : "৩ বাহু (হেরন)")}
              </button>
            ))}
            <div className="ml-auto"><UnitToggle value={triUnit} onChange={setTriUnit} lang={lang} /></div>
          </div>

          {triMethod === "bh" ? (
            <div className="grid gap-3 md:grid-cols-2">
              <NumField label={lang === "en" ? "Base" : "ভূমি"} v={triBase} on={setTriBase} useBn={useBn} />
              <NumField label={lang === "en" ? "Height" : "উচ্চতা"} v={triHeight} on={setTriHeight} useBn={useBn} />
            </div>
          ) : (
            <div className="grid gap-3 md:grid-cols-3">
              <NumField label={lang === "en" ? "Side a" : "বাহু a"} v={triA} on={setTriA} useBn={useBn} />
              <NumField label={lang === "en" ? "Side b" : "বাহু b"} v={triB} on={setTriB} useBn={useBn} />
              <NumField label={lang === "en" ? "Side c" : "বাহু c"} v={triC} on={setTriC} useBn={useBn} />
            </div>
          )}
          <AreaResult sqft={triArea} fmt={fmt} useBn={useBn} lang={lang} />
        </TabsContent>

        {/* ===== Tab 3: Circle ===== */}
        <TabsContent value="circle" className="mt-6 space-y-4">
          <div className="grid gap-3 md:grid-cols-3">
            <div className="space-y-1.5">
              <label className={cn("block text-xs text-muted-foreground", lang === "bn" && "font-bn")}>
                {lang === "en" ? "Input as" : "ইনপুট"}
              </label>
              <div className="flex gap-2">
                {(["radius", "diameter"] as const).map((k) => (
                  <button
                    key={k}
                    type="button"
                    onClick={() => setCircKind(k)}
                    className={cn(
                      "rounded-md border px-3 py-1.5 text-xs",
                      circKind === k ? "border-accent bg-accent/10 text-accent" : "border-border bg-card",
                    )}
                  >
                    {k === "radius" ? (lang === "en" ? "Radius" : "ব্যাসার্ধ") : (lang === "en" ? "Diameter" : "ব্যাস")}
                  </button>
                ))}
              </div>
            </div>
            <NumField
              label={circKind === "radius" ? (lang === "en" ? "Radius" : "ব্যাসার্ধ") : (lang === "en" ? "Diameter" : "ব্যাস")}
              v={circVal}
              on={setCircVal}
              useBn={useBn}
            />
            <UnitToggle value={circUnit} onChange={setCircUnit} lang={lang} />
          </div>
          <AreaResult sqft={circ.area} fmt={fmt} useBn={useBn} lang={lang} />
          <div className="grid gap-3 sm:grid-cols-2">
            <Stat label={lang === "en" ? "Circumference" : "পরিধি"} value={`${fmt(circ.circumference)} ${lang === "en" ? "ft" : "ফুট"}`} />
            <Stat label={lang === "en" ? "Radius (ft)" : "ব্যাসার্ধ (ফুট)"} value={fmt(circ.radiusFt)} />
          </div>
        </TabsContent>

        {/* ===== Tab 4: Multi-room ===== */}
        <TabsContent value="rooms" className="mt-6 space-y-4">
          <div className="flex justify-end">
            <button
              type="button"
              onClick={addRoom}
              className="inline-flex items-center gap-1.5 rounded-md border border-border bg-card px-3 py-1.5 text-xs hover:border-accent"
            >
              <Plus size={12} /> {lang === "en" ? "Add room" : "রুম যোগ"}
            </button>
          </div>
          <div className="space-y-2">
            {rooms.map((r, i) => (
              <div key={r.id} className="grid grid-cols-12 items-center gap-2 rounded-xl border border-border bg-card p-2">
                <input
                  type="text"
                  value={r.name}
                  onChange={(e) => updateRoom(r.id, { name: e.target.value })}
                  className="col-span-3 rounded-md border border-border bg-background px-2 py-1.5 text-sm focus:border-accent focus:outline-none"
                />
                <input
                  type="text" inputMode="decimal"
                  value={useBn ? toBengaliNumerals(r.length) : String(r.length)}
                  onChange={(e) => updateRoom(r.id, { length: parseFloat(stripBn(e.target.value)) || 0 })}
                  className="col-span-2 rounded-md border border-border bg-background px-2 py-1.5 text-sm tabular-nums focus:border-accent focus:outline-none"
                  placeholder="L"
                />
                <input
                  type="text" inputMode="decimal"
                  value={useBn ? toBengaliNumerals(r.width) : String(r.width)}
                  onChange={(e) => updateRoom(r.id, { width: parseFloat(stripBn(e.target.value)) || 0 })}
                  className="col-span-2 rounded-md border border-border bg-background px-2 py-1.5 text-sm tabular-nums focus:border-accent focus:outline-none"
                  placeholder="W"
                />
                <select
                  value={r.unit}
                  onChange={(e) => updateRoom(r.id, { unit: e.target.value as LenUnit })}
                  className="col-span-2 rounded-md border border-border bg-background px-2 py-1.5 text-sm focus:border-accent focus:outline-none"
                >
                  <option value="ft">ft</option>
                  <option value="m">m</option>
                  <option value="cm">cm</option>
                  <option value="hath">{lang === "en" ? "hath" : "হাত"}</option>
                  <option value="in">in</option>
                </select>
                <span className="col-span-2 text-right text-xs tabular-nums text-muted-foreground">
                  {fmt(roomsResult.rows[i]?.sqft ?? 0)} sq ft
                </span>
                <button
                  type="button"
                  onClick={() => removeRoom(r.id)}
                  className="col-span-1 inline-flex items-center justify-center rounded-md p-1.5 text-muted-foreground hover:text-destructive"
                  aria-label="Remove room"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
          <AreaResult
            sqft={roomsResult.total}
            fmt={fmt}
            useBn={useBn}
            lang={lang}
            label={lang === "en" ? "Total area" : "মোট আয়তন"}
          />
        </TabsContent>

        {/* ===== Tab 5: Practical ===== */}
        <TabsContent value="practical" className="mt-6 space-y-5">
          <div className="flex flex-wrap gap-2">
            {(["flooring", "paint", "land", "carpet"] as SubTab[]).map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => setSubTab(s)}
                className={cn(
                  "rounded-full border px-3 py-1.5 text-xs font-medium",
                  subTab === s
                    ? "border-accent bg-accent text-accent-foreground"
                    : "border-border bg-card hover:border-accent/40",
                  lang === "bn" && "font-bn",
                )}
              >
                {{
                  flooring: { en: "Flooring (tiles)", bn: "টাইলস" },
                  paint:    { en: "Painting", bn: "রঙ" },
                  land:     { en: "Land cost", bn: "জমির দাম" },
                  carpet:   { en: "Carpet", bn: "কার্পেট" },
                }[s][lang]}
              </button>
            ))}
          </div>

          {subTab === "flooring" && (
            <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className="space-y-3">
              <div className="grid gap-3 md:grid-cols-3">
                <NumField label={lang === "en" ? "Floor area (sq ft)" : "মেঝে (বর্গফুট)"} v={flrSqft} on={setFlrSqft} useBn={useBn} />
                <NumField label={lang === "en" ? "Tile length (in)" : "টাইল দৈর্ঘ্য (ইঞ্চি)"} v={tileLen} on={setTileLen} useBn={useBn} />
                <NumField label={lang === "en" ? "Tile width (in)" : "টাইল প্রস্থ (ইঞ্চি)"} v={tileWid} on={setTileWid} useBn={useBn} />
                <NumField label={lang === "en" ? "Wastage %" : "অপচয় %"} v={tileWastage} on={setTileWastage} useBn={useBn} />
                <NumField label={lang === "en" ? "Price per tile (৳)" : "প্রতি টাইল (৳)"} v={tilePrice} on={setTilePrice} useBn={useBn} />
              </div>
              <div className="rounded-2xl border border-accent/40 bg-accent/5 p-5">
                <p className="text-sm text-muted-foreground">
                  {lang === "en" ? "Tile size:" : "টাইলের আয়তন:"} {fmt(flooring.tileSqft)} {lang === "en" ? "sq ft each" : "বর্গফুট প্রতি"}
                </p>
                <p className="mt-1 text-3xl font-bold tabular-nums md:text-4xl">
                  {num(flooring.totalTiles)} <span className="text-base font-normal text-muted-foreground">{lang === "en" ? "tiles" : "টাইলস"}</span>
                </p>
                <p className="text-xs text-muted-foreground">
                  ({fmt(flooring.baseTiles)} + {num(tileWastage)}% {lang === "en" ? "wastage" : "অপচয়"})
                </p>
                {parseFloat(tilePrice) > 0 && (
                  <p className="mt-2 text-lg font-semibold">{formatBDT(flooring.totalCost, useBn ? "bn" : "en", 0)}</p>
                )}
              </div>
            </motion.div>
          )}

          {subTab === "paint" && (
            <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className="space-y-3">
              <div className="grid gap-3 md:grid-cols-3">
                <NumField label={lang === "en" ? "Wall area (sq ft)" : "দেয়াল (বর্গফুট)"} v={wallSqft} on={setWallSqft} useBn={useBn} />
                <NumField label={lang === "en" ? "Coats" : "কোট"} v={coats} on={setCoats} useBn={useBn} />
                <NumField label={lang === "en" ? "Coverage (sq ft / L)" : "কভারেজ (বর্গফুট/লি)"} v={coverage} on={setCoverage} useBn={useBn} />
                <NumField label={lang === "en" ? "Can size (L)" : "কান (লি)"} v={canSize} on={setCanSize} useBn={useBn} />
                <NumField label={lang === "en" ? "Price per litre (৳)" : "প্রতি লিটার (৳)"} v={paintPrice} on={setPaintPrice} useBn={useBn} />
              </div>
              <div className="rounded-2xl border border-accent/40 bg-accent/5 p-5">
                <p className="text-sm text-muted-foreground">
                  {lang === "en" ? "Paint needed:" : "রঙ লাগবে:"} {fmt(paint.liters)} {lang === "en" ? "litres" : "লিটার"}
                </p>
                <p className="mt-1 text-3xl font-bold tabular-nums md:text-4xl">
                  {num(paint.cans)} <span className="text-base font-normal text-muted-foreground">{lang === "en" ? `× ${canSize}L cans` : `× ${num(canSize)} লি কান`}</span>
                </p>
                {parseFloat(paintPrice) > 0 && (
                  <p className="mt-2 text-lg font-semibold">{formatBDT(paint.totalCost, useBn ? "bn" : "en", 0)}</p>
                )}
                <p className="mt-2 text-xs text-muted-foreground">
                  {lang === "en"
                    ? "Typical BD coverage: Berger Robbialac ~ 280–350 sq ft/L · Asian Paints Apex ~ 300–360 · Roxy ~ 280–340."
                    : "সাধারণ কভারেজ: Berger Robbialac ~ ২৮০–৩৫০ বর্গফুট/লি · Asian Paints Apex ~ ৩০০–৩৬০ · Roxy ~ ২৮০–৩৪০।"}
                </p>
              </div>
            </motion.div>
          )}

          {subTab === "land" && (
            <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className="space-y-3">
              <div className="grid gap-3 md:grid-cols-2">
                <NumField label={lang === "en" ? "Area (sq ft)" : "জমি (বর্গফুট)"} v={landSqft} on={setLandSqft} useBn={useBn} />
                <NumField label={lang === "en" ? "Price per sq ft (৳)" : "প্রতি বর্গফুট (৳)"} v={landRate} on={setLandRate} useBn={useBn} />
              </div>
              <div className="rounded-2xl border border-accent/40 bg-accent/5 p-5">
                <p className={cn("text-xs uppercase tracking-wide text-muted-foreground", lang === "bn" && "font-bn")}>
                  {lang === "en" ? "Total land cost" : "মোট দাম"}
                </p>
                <p className="mt-1 text-3xl font-bold md:text-4xl">{formatBDT(landCost, useBn ? "bn" : "en", 0)}</p>
                <p className="mt-1 text-xs text-muted-foreground">
                  = {fmt(fromSqft(parseFloat(landSqft) || 0, "katha"))} katha = {fmt(fromSqft(parseFloat(landSqft) || 0, "decimal"))} decimal
                </p>
              </div>
              <p className="text-xs text-muted-foreground">
                {lang === "en"
                  ? "Need full BD land-unit conversion (bigha, kani, gonda)? "
                  : "সম্পূর্ণ জমির একক রূপান্তর (বিঘা, কানি, গণ্ডা)? "}
                <Link to="/land-calculator" className="text-accent hover:underline">
                  {lang === "en" ? "Open Land Calculator →" : "জমি ক্যালকুলেটর →"}
                </Link>
              </p>
            </motion.div>
          )}

          {subTab === "carpet" && (
            <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className="space-y-3">
              <div className="grid gap-3 md:grid-cols-3">
                <NumField label={lang === "en" ? "Room area (sq ft)" : "রুম (বর্গফুট)"} v={carpetSqft} on={setCarpetSqft} useBn={useBn} />
                <NumField label={lang === "en" ? "Carpet roll width (ft)" : "কার্পেট প্রস্থ (ফুট)"} v={carpetWidth} on={setCarpetWidth} useBn={useBn} />
                <NumField label={lang === "en" ? "Price per running ft (৳)" : "প্রতি রানিং ফুট (৳)"} v={carpetPrice} on={setCarpetPrice} useBn={useBn} />
              </div>
              <div className="rounded-2xl border border-accent/40 bg-accent/5 p-5">
                <p className="text-sm text-muted-foreground">
                  {lang === "en" ? "Length needed:" : "দৈর্ঘ্য লাগবে:"}
                </p>
                <p className="mt-1 text-3xl font-bold tabular-nums md:text-4xl">
                  {fmt(carpet.lengthFt)} <span className="text-base font-normal text-muted-foreground">{lang === "en" ? "ft" : "ফুট"}</span>
                </p>
                {parseFloat(carpetPrice) > 0 && (
                  <p className="mt-2 text-lg font-semibold">{formatBDT(carpet.totalCost, useBn ? "bn" : "en", 0)}</p>
                )}
              </div>
            </motion.div>
          )}
        </TabsContent>
      </Tabs>

      {/* Educational content */}
      <article className="mt-12 space-y-10 print:hidden">
        <Section title={lang === "en" ? "How to calculate square feet" : "স্কয়ার ফিট কীভাবে বের করবেন"} lang={lang}>
          <ul className={cn("ml-5 list-disc space-y-1 text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
            <li>{lang === "en" ? "Rectangle / Square: length × width (in feet)." : "আয়তক্ষেত্র: দৈর্ঘ্য × প্রস্থ (ফুটে)।"}</li>
            <li>{lang === "en" ? "Triangle: ½ × base × height — or Heron's formula if you only have side lengths." : "ত্রিভুজ: ½ × ভূমি × উচ্চতা — অথবা শুধু বাহু জানা থাকলে হেরন সূত্র।"}</li>
            <li>{lang === "en" ? "Circle: π × radius². Diameter ÷ 2 = radius." : "বৃত্ত: π × ব্যাসার্ধ²। ব্যাস ÷ ২ = ব্যাসার্ধ।"}</li>
            <li>{lang === "en" ? "Irregular shape: split into rectangles + triangles, sum each." : "অনিয়মিত আকার: আয়তক্ষেত্র + ত্রিভুজে ভাগ করে যোগ করুন।"}</li>
          </ul>
        </Section>

        <Section title={lang === "en" ? "Square feet to other area units" : "বর্গফুট থেকে অন্য একক"} lang={lang}>
          <div className="overflow-x-auto rounded-xl border border-border">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-xs uppercase text-muted-foreground">
                <tr>
                  <th className="p-2 text-left">{lang === "en" ? "Unit" : "একক"}</th>
                  <th className="p-2 text-right">{lang === "en" ? "Value (1 sq ft = …)" : "মান (১ বর্গফুট = …)"}</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ["Square Meter (m²)",     fromSqft(1, "sqm")],
                  ["Square Yard (sq yd)",   fromSqft(1, "sqyd")],
                  ["Square Hath (বর্গহাত)", fromSqft(1, "sqhath")],
                  ["Decimal / Shotok",      fromSqft(1, "decimal")],
                  ["Katha",                 fromSqft(1, "katha")],
                  ["Bigha",                 fromSqft(1, "bigha")],
                  ["Acre",                  fromSqft(1, "acre")],
                  ["Hectare",               fromSqft(1, "hectare")],
                ].map(([k, v]) => (
                  <tr key={k as string} className="border-t border-border">
                    <td className="p-2">{k}</td>
                    <td className="p-2 text-right tabular-nums">{(v as number).toExponential ? (v as number).toPrecision(6) : v}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-2 text-xs text-muted-foreground">
            {lang === "en"
              ? "For full bigha / kani / gonda conversions across BD regional units, use the "
              : "সম্পূর্ণ বিঘা / কানি / গণ্ডা রূপান্তরের জন্য দেখুন "}
            <Link to="/land-calculator" className="text-accent hover:underline">
              {lang === "en" ? "Land Calculator" : "জমি ক্যালকুলেটর"}
            </Link>.
          </p>
        </Section>

        <Section title={lang === "en" ? "Cubit (হাত) — measurement in Bangladesh" : "হাত — বাংলাদেশের প্রাচীন মাপ"} lang={lang}>
          <p className={cn("text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
            {lang === "en"
              ? "1 hath ≈ 1.5 ft (the distance from elbow to fingertip). Older Bangladeshi homes, mosques and madrasahs were often built and measured in hath. 1 sq hath = 2.25 sq ft. A 30 hath × 20 hath room = 600 sq hath = 1,350 sq ft."
              : "১ হাত ≈ ১.৫ ফুট (কনুই থেকে মধ্যমার ডগা)। পুরোনো বাড়ি, মসজিদ ও মাদরাসা সাধারণত হাতে মাপা হত। ১ বর্গহাত = ২.২৫ বর্গফুট। ৩০ × ২০ হাত রুম = ৬০০ বর্গহাত = ১,৩৫০ বর্গফুট।"}
          </p>
        </Section>

        <Section title={lang === "en" ? "Flooring estimator — choosing tile size & wastage" : "টাইলস হিসাব — সাইজ ও অপচয়"} lang={lang}>
          <p className={cn("text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
            {lang === "en"
              ? "Common BD tile sizes: 12\"×12\" (1 sq ft), 16\"×16\" (1.78 sq ft), 18\"×18\" (2.25 sq ft), 24\"×24\" (4 sq ft). Bigger tiles need fewer pieces but more cuts at edges. Standard wastage: 10% straight layout, 15% diagonal. Buy from same lot/batch to avoid colour variation."
              : "প্রচলিত টাইল সাইজ: ১২\"×১২\" (১ বর্গফুট), ১৬\"×১৬\" (১.৭৮), ১৮\"×১৮\" (২.২৫), ২৪\"×২৪\" (৪)। বড় টাইলে সংখ্যা কম লাগে কিন্তু কাটা বেশি। অপচয়: সরল বিছানে ১০%, কোণাকুণি ১৫%। একই লট/ব্যাচ থেকে কিনুন।"}
          </p>
        </Section>

        <Section title={lang === "en" ? "Paint coverage — Berger, Asian Paints, Roxy" : "রঙের কভারেজ — Berger, Asian Paints, Roxy"} lang={lang}>
          <ul className={cn("ml-5 list-disc space-y-1 text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
            <li>{lang === "en" ? "Berger Robbialac Emulsion: ~ 280–350 sq ft/L per coat (smooth surface)." : "Berger Robbialac Emulsion: ~ ২৮০–৩৫০ বর্গফুট/লি প্রতি কোট।"}</li>
            <li>{lang === "en" ? "Asian Paints Apex / Royale: ~ 300–360 sq ft/L per coat." : "Asian Paints Apex / Royale: ~ ৩০০–৩৬০ বর্গফুট/লি প্রতি কোট।"}</li>
            <li>{lang === "en" ? "Roxy Paints / Elite: ~ 280–340 sq ft/L per coat." : "Roxy Paints / Elite: ~ ২৮০–৩৪০ বর্গফুট/লি প্রতি কোট।"}</li>
            <li>{lang === "en" ? "Plan for 2 coats. Rough surfaces (plaster) reduce coverage by 20–30%." : "২ কোট ধরুন। রুক্ষ পৃষ্ঠ (প্লাস্টার) কভারেজ ২০–৩০% কমিয়ে দেয়।"}</li>
          </ul>
        </Section>

        <Section title={lang === "en" ? "Renovation cost — quick reference (BD, 2025)" : "রেনোভেশন খরচ — দ্রুত রেফারেন্স (বাংলাদেশ, ২০২৫)"} lang={lang}>
          <ul className={cn("ml-5 list-disc space-y-1 text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
            <li>{lang === "en" ? "Floor tiles + labour: ৳ 80–250 / sq ft (depends on tile grade)." : "মেঝে টাইলস + শ্রম: ৳ ৮০–২৫০ / বর্গফুট।"}</li>
            <li>{lang === "en" ? "Wall paint + labour: ৳ 18–35 / sq ft (2 coats)." : "দেয়াল রঙ + শ্রম: ৳ ১৮–৩৫ / বর্গফুট (২ কোট)।"}</li>
            <li>{lang === "en" ? "False ceiling (gypsum board): ৳ 90–160 / sq ft." : "ফলস সিলিং (জিপসাম): ৳ ৯০–১৬০ / বর্গফুট।"}</li>
            <li>{lang === "en" ? "Always get 2–3 quotations and verify materials." : "২–৩টি কোটেশন নিন এবং মালামাল যাচাই করুন।"}</li>
          </ul>
        </Section>

        <Section title={lang === "en" ? "Related calculators" : "সম্পর্কিত ক্যালকুলেটর"} lang={lang}>
          <div className="flex flex-wrap gap-2 text-sm">
            <Link to="/land-calculator" className="rounded-md border border-border px-3 py-1.5 hover:border-accent hover:text-accent">
              {lang === "en" ? "Land" : "জমি"}
            </Link>
            <Link to="/percentage-calculator" className="rounded-md border border-border px-3 py-1.5 hover:border-accent hover:text-accent">
              {lang === "en" ? "Percentage" : "শতকরা"}
            </Link>
            <Link to="/loan-calculator" className="rounded-md border border-border px-3 py-1.5 hover:border-accent hover:text-accent">
              {lang === "en" ? "Loan" : "লোন"}
            </Link>
          </div>
        </Section>
      </article>
    </CalculatorLayout>
  );
}

// ====== Helpers / sub-components ======

function stripBn(s: string): string {
  return s.replace(/[০-৯]/g, (d) => "০১২৩৪৫৬৭৮৯".indexOf(d).toString());
}

function NumField({
  label, v, on, useBn,
}: { label: string; v: string; on: (v: string) => void; useBn: boolean }) {
  return (
    <div className="space-y-1.5">
      <label className="block text-xs text-muted-foreground">{label}</label>
      <input
        type="text"
        inputMode="decimal"
        value={useBn ? toBengaliNumerals(v) : v}
        onChange={(e) => on(stripBn(e.target.value))}
        className="w-full rounded-md border border-border bg-card px-3 py-2 text-sm tabular-nums focus:border-accent focus:outline-none"
      />
    </div>
  );
}

function UnitToggle({
  value, onChange, lang,
}: { value: LenUnit; onChange: (u: LenUnit) => void; lang: Lang }) {
  const opts: { id: LenUnit; label: string }[] = [
    { id: "ft",   label: lang === "en" ? "ft" : "ফুট" },
    { id: "m",    label: "m" },
    { id: "cm",   label: "cm" },
    { id: "in",   label: "in" },
    { id: "hath", label: lang === "en" ? "hath" : "হাত" },
  ];
  return (
    <div className="space-y-1.5">
      <label className={cn("block text-xs text-muted-foreground", lang === "bn" && "font-bn")}>
        {lang === "en" ? "Unit" : "একক"}
      </label>
      <div className="flex flex-wrap gap-1">
        {opts.map((o) => (
          <button
            key={o.id}
            type="button"
            onClick={() => onChange(o.id)}
            className={cn(
              "rounded-md border px-2.5 py-1.5 text-xs",
              value === o.id ? "border-accent bg-accent/10 text-accent" : "border-border bg-card hover:border-accent/40",
            )}
          >
            {o.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function AreaResult({
  sqft, fmt, useBn, lang, label,
}: { sqft: number; fmt: (n: number, d?: number) => string; useBn: boolean; lang: Lang; label?: string }) {
  if (!Number.isFinite(sqft) || sqft <= 0) {
    return (
      <div className="rounded-2xl border border-border bg-card p-5 text-center text-sm text-muted-foreground">
        {lang === "en" ? "Enter dimensions to see area." : "মাপ দিন, আয়তন দেখাবে।"}
      </div>
    );
  }
  return (
    <motion.div
      key={sqft}
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      className="rounded-2xl border border-accent/40 bg-accent/5 p-5"
    >
      <p className={cn("text-xs uppercase tracking-wide text-muted-foreground", lang === "bn" && "font-bn")}>
        {label ?? (lang === "en" ? "Area" : "আয়তন")}
      </p>
      <p className="mt-1 text-3xl font-bold tabular-nums md:text-4xl">
        {fmt(sqft)} <span className="text-base font-normal text-muted-foreground">{lang === "en" ? "sq ft" : "বর্গফুট"}</span>
      </p>
      <p className="mt-1 text-sm text-muted-foreground">
        = {fmt(fromSqft(sqft, "sqm"))} {lang === "en" ? "sq m" : "বর্গমিটার"}
        {" "}· = {fmt(fromSqft(sqft, "sqhath"))} {lang === "en" ? "sq hath" : "বর্গহাত"}
        {" "}· = {fmt(fromSqft(sqft, "katha"), 4)} {lang === "en" ? "katha" : "কাঠা"}
        {" "}· = {fmt(fromSqft(sqft, "decimal"), 4)} {lang === "en" ? "decimal" : "ডেসিমেল"}
      </p>
      {useBn && (
        <p className="mt-1 font-bn text-xs text-muted-foreground">
          {toBengaliNumerals(fmt(sqft))} বর্গফুট
        </p>
      )}
    </motion.div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl border border-border bg-card p-3">
      <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="mt-0.5 text-base font-semibold tabular-nums">{value}</p>
    </div>
  );
}

function Section({
  title, children, lang,
}: { title: string; children: React.ReactNode; lang: Lang }) {
  return (
    <section className="space-y-3">
      <h2 className={cn("text-lg font-semibold tracking-tight", lang === "bn" && "font-bn")}>{title}</h2>
      {children}
    </section>
  );
}
